import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import {
  Button as AsurionButton,
  Text,
  ButtonVariant,
  Icon,
  Size
} from "@trailhead/asurion-ui";
import { trackEvent } from "@trailhead/analytics";
import SelfServicePNG from "../assets/SelfServiceImageFull.png";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { navigateToUrl } from "single-spa";
import { useQueryParams } from "@trailhead/smart-setup-core";

const breakpointSm = `(max-width: ${Size.SM - 1}px)`;

type CardProps = {
  showBorder: boolean;
};

const CardContainer = styled.div<CardProps>`
  ${(props: CardProps) =>
    props.showBorder &&
    css`
      border: 1px solid #a5aaaf;
      border-radius: 8px;
    `}

  display: flex;
  flex-direction: row;
  align-items: flex-start;

  align-self: stretch;
  margin-bottom: 2rem;

  position: relative;

  -webkit-box-sizing: border-box; /* Safari/Chrome, other WebKit */
  -moz-box-sizing: border-box; /* Firefox, other Gecko */
  box-sizing: border-box; /* Opera/IE 8+ */
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 296px;
  padding: 24px 0px 24px 24px;

  @media (${breakpointSm}) {
    width: 246px;
  }

  -webkit-box-sizing: border-box; /* Safari/Chrome, other WebKit */
  -moz-box-sizing: border-box; /* Firefox, other Gecko */
  box-sizing: border-box; /* Opera/IE 8+ */
`;

const ContentWrapper = styled(Text)`
  line-height: 150%;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  margin-bottom: 16px;

  -webkit-box-sizing: border-box; /* Safari/Chrome, other WebKit */
  -moz-box-sizing: border-box; /* Firefox, other Gecko */
  box-sizing: border-box; /* Opera/IE 8+ */
`;

const MainHeader = styled(Text)`
  margin-bottom: 12px;
`;

const ImageWrapper = styled.div`
  flex: 0 1 auto;
  flex-grow: 1;
  position: relative;
  display: flex;
  flex-direction: row;
  align-self: stretch;
  justify-content: center;

  -webkit-box-sizing: border-box; /* Safari/Chrome, other WebKit */
  -moz-box-sizing: border-box; /* Firefox, other Gecko */
  box-sizing: border-box; /* Opera/IE 8+ */
`;

const ImageContainer = styled.img`
  position: absolute;
  left: 50%;
  top: 50%;
  height: 100%;
  z-index: -1;

  -webkit-transform: translateY(-50%) translateX(-50%);
  -moz-transform: translateY(-50%) translateX(-50%);
  transform: translateY(-50%) translateX(-50%);
`;

const IconContainer = styled.div`
  padding-left: 0.75rem;
  display: flex;
  justify-content: center;
  align-items: center;

  -webkit-box-sizing: border-box; /* Safari/Chrome, other WebKit */
  -moz-box-sizing: border-box; /* Firefox, other Gecko */
  box-sizing: border-box; /* Opera/IE 8+ */
`;

interface IntroHeaderProps {
  buttonVariant?: ButtonVariant;
  containerBorder?: boolean;
}

export const SetupDeviceCTA: React.FC<IntroHeaderProps> = ({
  buttonVariant = "default",
  containerBorder = false,
}) => {
  const t = useSanitizedTranslation();
  const queryParams = useQueryParams();
  const [isSmallDevice, setIsSmallDevice] = useState(
    window.matchMedia(breakpointSm).matches,
  );

  useEffect(() => {
    window
      .matchMedia(breakpointSm)
      .addEventListener("change", e => setIsSmallDevice(e.matches));
  }, []);

  return (
    <CardContainer showBorder={containerBorder}>
      <ContentContainer>
        <HeaderContainer>
          <MainHeader size={isSmallDevice ? 4 : 5} weight={"feather"}>
            {t("Set up your device the easy way")}
          </MainHeader>
          <ContentWrapper size={2}>
            {t(
              "We’ll walk you through the steps to make sure everything works seamlessly.",
            )}
          </ContentWrapper>
        </HeaderContainer>
        <AsurionButton
          color={"secondary"}
          variant={buttonVariant}
          onClick={() => {
            trackEvent("SelfGuideNavigationButtonClick");
            queryParams.set("skipPathfinder", "true");

            navigateToUrl(
              `/mobile-device-setup/intro?${queryParams.toString()}`,
            );
          }}
        >
          <>
            {t("Use the setup guide")}
            <IconContainer>
              <Icon
                size="large"
                src="ArrowRight"
                fill={buttonVariant === "outline" ? "#000" : "#fff"}
              />
            </IconContainer>
          </>
        </AsurionButton>
      </ContentContainer>
      <ImageWrapper>
        <ImageContainer src={SelfServicePNG} />
      </ImageWrapper>
    </CardContainer>
  );
};
